/**
 * Pure i18n logic. NO React imports so this module can be unit-tested
 * from the root vitest runner (which has no React installed; that's in
 * web/node_modules only). React-dependent Provider + hook live in
 * `i18n.ts` and re-export from here. See memory:
 * feedback_root_typecheck.md for why the split exists.
 */

export const DEFAULT_LOCALE = "en-GB";
export const SUPPORTED_LOCALES = ["en-GB", "id-ID"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export function isSupportedLocale(x: unknown): x is SupportedLocale {
  return typeof x === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(x);
}

// ── Catalogue types ──────────────────────────────────────────────────────
export type TranslationCatalogue = {
  [key: string]: string | TranslationCatalogue;
};

// ── Registry (populated on init) ─────────────────────────────────────────
const catalogues: Partial<Record<SupportedLocale, TranslationCatalogue>> = {};

/** Register a catalogue for a locale. Called at app-bootstrap. */
export function registerCatalogue(locale: SupportedLocale, catalogue: TranslationCatalogue): void {
  catalogues[locale] = catalogue;
}

// ── Lookup + interpolation ───────────────────────────────────────────────

function resolveKey(catalogue: TranslationCatalogue | undefined, key: string): string | undefined {
  if (!catalogue) return undefined;
  const parts = key.split(".");
  let cursor: TranslationCatalogue | string | undefined = catalogue;
  for (const part of parts) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as TranslationCatalogue)[part];
  }
  return typeof cursor === "string" ? cursor : undefined;
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name) => {
    const v = params[name];
    return v == null ? match : String(v);
  });
}

function pluralise(
  catalogue: TranslationCatalogue | undefined,
  key: string,
  count: number,
): string | undefined {
  if (!catalogue) return undefined;
  const parts = key.split(".");
  let cursor: TranslationCatalogue | string | undefined = catalogue;
  for (const part of parts) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as TranslationCatalogue)[part];
  }
  if (typeof cursor === "string") return cursor;
  if (typeof cursor === "object" && cursor !== null) {
    const bucket = count === 1 ? "one" : "other";
    const value = (cursor as TranslationCatalogue)[bucket];
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
}

/**
 * Translate a key. Tries the requested locale, falls back to en-GB, then
 * the raw key. Params are interpolated as `{{name}}`; a `count` param
 * enables pluralisation.
 */
export function translate(
  locale: SupportedLocale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const count = params && typeof params.count === "number" ? params.count : undefined;

  const lookup = count != null
    ? (loc: SupportedLocale) => pluralise(catalogues[loc], key, count)
    : (loc: SupportedLocale) => resolveKey(catalogues[loc], key);

  let raw = lookup(locale);
  if (raw == null && locale !== DEFAULT_LOCALE) raw = lookup(DEFAULT_LOCALE);
  if (raw == null) return key;
  return interpolate(raw, params);
}

// ── Locale resolution ────────────────────────────────────────────────────

export type LocaleInputs = {
  userLocale?: string | null;
  venueLocale?: string | null;
  browserLocale?: string | null;
};

/**
 * Pick the locale to use given the resolution inputs. Any input that
 * isn't in SUPPORTED_LOCALES is discarded (soft fallback so an
 * unsupported user preference doesn't crash the app).
 */
export function resolveLocale({ userLocale, venueLocale, browserLocale }: LocaleInputs): SupportedLocale {
  for (const candidate of [userLocale, venueLocale, browserLocale]) {
    if (isSupportedLocale(candidate)) return candidate;
    if (typeof candidate === "string") {
      const twoLetter = candidate.split("-")[0].toLowerCase();
      if (twoLetter === "id") return "id-ID";
      if (twoLetter === "en") return "en-GB";
    }
  }
  return DEFAULT_LOCALE;
}
